import React from "react";
import { money, fmtDate } from "@/lib/format";
import { Boxes, Package, MessageCircle } from "lucide-react";

const mhFmt = (v) => `${(Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} MH`;
const num = (v) => (Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// Hover bill-of-materials for a takeoff layer. Shows each resolved leaf part with its ABSOLUTE
// quantity (scaled to what's measured on the sheet), plus man-hours and material $ per part.
export function BomCard({ bom }) {
  if (!bom) return null;
  const { name, uom, total, mult, count, mh: totMh, material, hasParts, parts = [], overridden } = bom;
  return (
    <div className="w-96 max-w-[94vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden text-xs" data-testid="bom-card">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
        <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
          <Boxes className="w-3.5 h-3.5 text-[#003E87] shrink-0" />
          <span className="truncate">{name}</span>
          {overridden && <span className="ml-auto text-[9px] text-[#F4C400] font-semibold shrink-0">edited</span>}
        </div>
        <div className="text-[10px] text-slate-400 font-mono mt-0.5">
          {num(total)} {uom}{mult !== 1 ? ` ×${mult}` : ""}
          {hasParts ? ` · ${num(count)} ${count === 1 ? "assembly" : "assemblies"}` : ""}
        </div>
      </div>
      {hasParts && parts.length > 0 ? (
        <div className="max-h-[46vh] overflow-y-auto overflow-x-hidden">
          <table className="w-full table-fixed border-collapse">
            <colgroup><col /><col className="w-20" /><col className="w-16" /><col className="w-20" /></colgroup>
            <thead>
              <tr className="text-[9px] uppercase tracking-wide text-slate-400 bg-white sticky top-0">
                <th className="text-left font-medium px-3 py-1">Part</th>
                <th className="text-right font-medium px-1 py-1">Qty</th>
                <th className="text-right font-medium px-1 py-1">MH</th>
                <th className="text-right font-medium px-3 py-1">Mat'l</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p, i) => (
                <tr key={i} className="border-t border-slate-50 align-top" data-testid={`bom-part-${i}`}>
                  <td className="px-3 py-1 text-slate-700 overflow-hidden">
                    <div className="truncate">{p.name}</div>
                    {p.brand ? <div className="text-[9px] text-slate-400 truncate">{p.brand}</div> : null}
                  </td>
                  <td className="px-1 py-1 text-right font-mono text-[10px] text-slate-800 whitespace-nowrap tabular-nums">{num(p.qty)}</td>
                  <td className="px-1 py-1 text-right font-mono text-[10px] text-slate-500 whitespace-nowrap tabular-nums">{num(p.mh)}</td>
                  <td className="px-3 py-1 text-right font-mono text-[10px] text-slate-500 whitespace-nowrap tabular-nums">{money(p.material)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-3 py-2.5 text-[11px] text-slate-500 flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5 text-slate-400" /> Direct line item — no sub-parts.
        </div>
      )}
      <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between bg-slate-50/70">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Total</span>
        <span className="font-mono font-semibold text-slate-800">{mhFmt(totMh)}{material ? ` · ${money(material)}` : ""}</span>
      </div>
    </div>
  );
}

// Hover card for a markup/annotation's comment thread — mirrors BomCard's shell exactly so the
// takeoff & annotation comment previews read identically to the line-item (BOM) hover.
export function CommentCard({ items, count }) {
  const loading = items == null;
  const n = count ?? (items ? items.length : 0);
  return (
    <div className="w-80 max-w-[94vw] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden text-xs" data-testid="comment-hover-card">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
        <div className="font-semibold text-slate-800 flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5 text-[#003E87] shrink-0" />
          <span>Comments</span>
          <span className="ml-auto text-[10px] text-slate-400 font-mono">{n}</span>
        </div>
      </div>
      <div className="max-h-[46vh] overflow-y-auto overflow-x-hidden divide-y divide-slate-50">
        {loading ? (
          <div className="px-3 py-3 text-[11px] text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-3 py-3 text-[11px] text-slate-400">No comments yet.</div>
        ) : items.map((c) => (
          <div key={c.comment_id} className="px-3 py-2 flex gap-2" data-testid={`comment-hover-item-${c.comment_id}`}>
            <div className="w-6 h-6 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-[10px] font-semibold shrink-0">
              {(c.author_name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-slate-400">{c.author_name}{c.created_at ? ` · ${fmtDate(c.created_at)}` : ""}</div>
              <div className="text-[11px] text-slate-700 whitespace-pre-wrap break-words">{c.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
